﻿<script>
  import { onMount } from "svelte/internal";
  import {
    dispatchMountEvent,
    compareCSVHeaders,
    getCSVColumnIndex,
    getCSVDisplayableHeaders
  } from "./services";
  import CSVUpload from "./CSVUpload.svelte";
  import SortableTable from "./SortableTable.svelte";
  import Icon from "./Icons.svelte";

  export let _emitMount;
  export let name;
  export let samplefile;

  // const
  let csvHeaders = [
    { name: "Request ID", required: true },
    { name: "Customer Name", required: false },
    { name: "Request Type", required: false },
    { name: "Enquiry Type", required: false },
    { name: "Customer Request", required: false },
    { name: "Buyback Balance", required: false },
    { name: "Approve Buyback", required: false },
    { name: "Buyback Approval", required: false },
    { name: "New Comment", required: true }
  ];

  // state
  let modelElement;
  let isLoading = false;
  let requestList = [];
  let errors = [];
  let fileContent = [];
  let tableHeader = [];
  let tableBody = [];
  let dataValid = false;

  onMount(() => {
    if (_emitMount) {
      dispatchMountEvent();
    }
  });

  $: {
    errors = [];
    tableHeader = [];
    tableBody = [];
    if (fileContent && fileContent.length > 0) {
      // validate data
      const rawHeaders = fileContent[0];
      const missingHeaders = compareCSVHeaders(rawHeaders, csvHeaders);

      if (missingHeaders.length) {
        errors = ["Missing " + missingHeaders.join(", ") + " column(s)"];
      } else {
        csvHeaders = getCSVDisplayableHeaders(rawHeaders, csvHeaders);
        const headerIndex = getCSVColumnIndex(rawHeaders, csvHeaders);

        tableHeader = csvHeaders.map(x => x.name);

        // prepare body
        const bodyData = [];
        for (let i = 1; i < fileContent.length; i++) {
          const row = fileContent[i];
          const newRow = headerIndex.map(i => row[i]);

          // Format Buyback Balance as GBP currency
          const buybackIndex = csvHeaders.findIndex(h => h.name === "Buyback Balance");
          if (buybackIndex !== -1) {
            let val = newRow[buybackIndex];
            let num = parseFloat(val?.toString().replace(/[^0-9.-]+/g, ""));
            if (!isNaN(num)) {
              newRow[buybackIndex] = `£${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            } else {
              newRow[buybackIndex] = "£0.00";
            }
          }

          // trim comment
          const commentIndex = newRow.length - 1;
          if (newRow[commentIndex] && newRow[commentIndex].length > 30) {
            newRow[commentIndex] =
              newRow[commentIndex].substring(0, 30) + "...";
          }

          bodyData.push(newRow);
        }

        tableBody = bodyData;
      }
    }
  }
  $: {
    if (tableBody.length || tableHeader.length) {
      errors = [];
      dataValid = true;
    }
  }

  $: {
    if (errors.length) {
      dataValid = false;
    }
  }

  const fileSelected = content => {
    fileContent = content;
  };
</script>

<div class="container container-lg-fluid container--no-gutters-mobile">
  <div class="card card--edge">
    <div class="card-header">
      <div class="row align-items-center">
        <div class="col-md">
          <h4 class="card-title mb-0">Import responses via .csv file</h4>
        </div>
        {#if samplefile && samplefile.length}
          <div class="col-auto">
            <a href={samplefile} target="_blank" class="btn-icon-start">
              <Icon icon="Download" class="icon icon-sm" />
              Download sample .csv
            </a>
          </div>
        {/if}
      </div>
    </div>
    <div class="card-body">
      <CSVUpload
        {name}
        maxsize="10"
        onFileSelected={fileSelected}
        {dataValid} />
    </div>
  </div>

  {#if errors.length}
    <div class="pt-4 pt-3" />
    <div class="alert alert-danger mb-0" role="alert">
      <div class="row no-gutters">
        <div class="col-auto">
          <Icon icon="XSolid" class="icon alert-icon" />
        </div>
        <div class="col">
          <h6 class="mb-2">
            The file was not imported. The following could not be processed:
          </h6>
          <ul class="m-0 p-0 pl-3">
            {#each errors as error}
              <li class="h6 mb-0 py-1">{error}</li>
            {/each}
          </ul>
        </div>
      </div>
    </div>
  {/if}
</div>
{#if tableHeader.length || tableBody.length}
  <div class="space-separator--md" />
  <div class="container container-max-fluid container--no-gutters-mobile">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0 font-italic">
          {tableBody.length} {tableBody.length === 1 ? 'Result' : 'Results'}
        </h5>
      </div>
      <div class="card-body p-0">
        <SortableTable
          header={tableHeader}
          body={tableBody}
          class="table-height-md table-sticky-header" />
      </div>
    </div>
  </div>
{/if}
